import { computed, observable, action, makeObservable } from 'mobx';
import { v4 as uuid } from 'uuid';
import isNil from 'lodash/isNil';
import groupBy from 'lodash/groupBy';

import {
    AuthUserData,
    UserData,
    EntryTypeListItemData,
    EntryTypeValueData,
    SipConfigurationsData,
    PaymentMethodOptionViewModel,
    UserProfileData,
} from 'chaika-frontend-types/frontend-cms';
import type { Optional } from 'chaika-common-types';
import { Locale } from 'chaika-enums';

import { SortingOrder } from '@store/types';

import { OrderParams } from '@store/abstract/List';
import { User } from '@store/models/User';
import { EntryFilterParams } from '@patient/store/ehr/models/EntryList';
import { SubscriptionList } from '@store/models/SubscriptionList';
import { EntryTypeFieldTemplateList } from './models/EntryTypeFieldTemplateList';
import { EntryTypeCommonTemplateList } from './models/EntryTypeCommonTemplateList';
import { Favorites } from './models/Favorites';
import { MostPopular } from './models/MostPopular';
import { isDefined } from '@utils/isDefined';
import { assertIsDefined } from '@utils/assertIsDefined';

interface PaymentGuaranteeFiltersParams {
    query: string;
    creationTimeFrom: string;
    creationTimeTo: string;
}

interface GuaranteeFiltersParams extends PaymentGuaranteeFiltersParams {
    organizationIds: Optional<string[]>;
}

type AuthUserConstructorParams = AuthUserData &
    UserData &
    UserProfileData & {
        organizationId?: string;
        officeId?: string;
        paymentWorkplaceIdsByClinic?: Record<string, string>;
    };

export class AuthUser extends User {
    public static Name = 'authUser' as const;

    @observable public organizationId: Optional<string>;
    @observable public legalEntityId: Optional<string>;
    @observable public mostPopular = new MostPopular();
    @observable public favorites = new Favorites();
    @observable public officeId: Optional<string>;
    @observable public practiceClinicIds: string[] = [];
    @observable public scheduleSettingsSpecialtyIds: string[] = [];
    @observable public userServiceIds: string[] = [];
    @observable public authorizationTimeLeft?: number;
    @observable public lastViewedEntryId?: string;
    @observable public patientSubscriptions = new SubscriptionList();
    @observable public sipAccount?: Omit<SipConfigurationsData, 'websocket'> & { organizationId: string };
    @observable public paymentMethods: PaymentMethodOptionViewModel[] = [];
    @observable public paymentWorkplaceIdsByClinic: Record<string, string> = {};
    @observable public selectedLanguage?: Locale;

    @observable public guaranteeFilters: GuaranteeFiltersParams = {
        query: '',
        organizationIds: [],
        creationTimeFrom: String(new Date()),
        creationTimeTo: String(new Date()),
    };

    @observable public mainPracticeDoctorIds: string[] = [];

    @observable public entryTypeFieldTemplates = new Map<string, EntryTypeFieldTemplateList>();
    @observable protected entryTypeCommonTemplates = new Map<string, EntryTypeCommonTemplateList>();
    @observable protected entryListFilterParams: EntryFilterParams = {
        ehrId: '',
        entryTypes: [],
        authors: [],
        organizations: [],
        medicalCases: [],
        statuses: [],
    };

    @observable protected entryListOrderParams: OrderParams = {
        fieldName: 'creationTime',
        order: SortingOrder.DESC,
    };

    @observable private entryTypes: EntryTypeListItemData[] = [];

    constructor(
        {
            organizationId,
            officeId,
            legalEntityId,
            selectedLanguage,
            paymentWorkplaceIdsByClinic = {},
            ...user
        }: AuthUserConstructorParams = {} as AuthUserConstructorParams,
    ) {
        super(user);
        makeObservable(this);

        this.selectedLanguage = selectedLanguage;
        this.organizationId = organizationId;
        this.legalEntityId = legalEntityId;
        this.officeId = officeId;
        this.organizationId = organizationId;
        this.paymentWorkplaceIdsByClinic = paymentWorkplaceIdsByClinic;
    }

    @computed
    public get entryTypeTemplatesLoaded(): boolean {
        return this.entryTypeFieldTemplates.size > 0;
    }

    @computed
    public get userEntryTypes(): { id: string; version: number; title: string }[] {
        return this.entryTypes;
    }

    @computed
    public get isTimeLeft(): boolean {
        return (this.authorizationTimeLeft ?? 0) < 0;
    }

    @computed
    public get canChoosePaymentWorkplace(): boolean {
        return this.isDirector || this.isAssistant;
    }

    @computed
    public get isAuthorized(): boolean {
        return !isNil(this.email);
    }

    @action
    public initLocale(): void {
        if (!isDefined(this.selectedLanguage)) {
            this.selectedLanguage =
                Intl.DateTimeFormat().resolvedOptions().locale === Locale.Ru ? Locale.Ru : Locale.En;
        }
    }

    public getEntryListFilterParams(): EntryFilterParams {
        return this.entryListFilterParams;
    }

    public getEntryTypeFieldTemplateList(entryTypeId: string): EntryTypeFieldTemplateList {
        if (!this.entryTypeFieldTemplates.has(entryTypeId)) {
            this.entryTypeFieldTemplates.set(entryTypeId, new EntryTypeFieldTemplateList());
        }

        const templateList = this.entryTypeFieldTemplates.get(entryTypeId);
        assertIsDefined(templateList, `Field template list of entry type ${entryTypeId} is created above`);
        return templateList;
    }

    public getEntryTypeCommonTemplateList(entryTypeId: string): EntryTypeCommonTemplateList {
        if (!this.entryTypeCommonTemplates.has(entryTypeId)) {
            this.entryTypeCommonTemplates.set(entryTypeId, new EntryTypeCommonTemplateList());
        }
        const templateList = this.entryTypeCommonTemplates.get(entryTypeId);
        assertIsDefined(templateList, `Common template list of entry type ${entryTypeId} is created above`);
        return templateList;
    }

    @action
    public setEntryTypeFieldTemplates(templates: EntryTypeValueData[]): void {
        const grouped = groupBy(this.fixTemplatesIds(templates), 'entryTypeId');

        Object.keys(grouped).forEach(entryTypeId => {
            this.entryTypeFieldTemplates.set(entryTypeId, new EntryTypeFieldTemplateList(grouped[entryTypeId]));
        });
    }

    @action
    public setEntryTypeCommonTemplates(templates: EntryTypeValueData[]): void {
        const grouped = groupBy(this.fixTemplatesIds(templates), 'entryTypeId');

        Object.keys(grouped).forEach(entryTypeId => {
            this.entryTypeCommonTemplates.set(entryTypeId, new EntryTypeCommonTemplateList(grouped[entryTypeId]));
        });
    }

    @action
    public setEntryTypes(entryTypes: EntryTypeListItemData[]): void {
        this.entryTypes = entryTypes;
    }

    @action
    public setPracticeClinicIds(clinicIds: string[]): void {
        this.practiceClinicIds = clinicIds;
    }

    @action
    public setScheduleSettingsSpecialtyIds(specialtyIds: string[]): void {
        this.scheduleSettingsSpecialtyIds = specialtyIds;
    }

    @action
    public setUserServiceIds(serviceIds: string[]): void {
        this.userServiceIds = serviceIds;
    }

    @action
    public rememberPaymentWorkplace(clinicId: string, workplaceId: string): void {
        this.paymentWorkplaceIdsByClinic = { ...this.paymentWorkplaceIdsByClinic, [clinicId]: workplaceId };

        if (clinicId === this.organizationId) {
            this.officeId = workplaceId;
        }
    }

    @action
    public setMainPracticeDoctorIds(doctorIds: string[]): void {
        this.mainPracticeDoctorIds = doctorIds;
    }

    @action
    public setEntryListFilterParams(params: Partial<EntryFilterParams>): void {
        this.entryListFilterParams = { ...this.entryListFilterParams, ...params };
    }

    public getEntryListOrderParams(): OrderParams {
        return this.entryListOrderParams;
    }

    @action
    public setEntryListOrderParams(params: OrderParams): void {
        this.entryListOrderParams = params || ({} as OrderParams);
    }

    @action
    public setSelectedLang(lang: Locale) {
        this.selectedLanguage = lang;
    }

    @action
    public updateGuaranteeFilters(guaranteeFilters?: Partial<GuaranteeFiltersParams>): void {
        this.guaranteeFilters = {
            ...this.guaranteeFilters,
            ...guaranteeFilters,
        };
    }

    public isAuthInOrganization(organizationId: string): boolean {
        return this.organizationId === organizationId;
    }

    // В прошлой версии шаблонов можно было копировать и при этом создалавилсь шаблоны с теми же самыми айдишниками
    private fixTemplatesIds(templates: EntryTypeValueData[]): EntryTypeValueData[] {
        const usedIds = new Set<string>();
        return templates.map(({ templates, ...entryTypeTemplate }) => {
            const templatesWithUniqIds = templates.map(({ id, ...template }) => {
                const templateId = usedIds.has(id) ? uuid() : id;
                usedIds.add(templateId);
                return {
                    id: templateId,
                    ...template,
                };
            });

            return {
                templates: templatesWithUniqIds,
                ...entryTypeTemplate,
            };
        });
    }
}

export const authUser = new AuthUser();
